import React, { Component } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupText,
  Row,
  Col,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Draggable from 'react-draggable';
import moment from 'moment';

import { FetchData } from './utils/DataFetch';
import { GetCookie, Timeout } from './utils/Common';

const resumeFilePrefixPattern =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;
const signUpStatusOptions = [
  'Needs Review',
  'Reviewed - Accepted',
  'Reviewed - Rejected',
];
const languageSplitPattern = /[,;\r\n]+/;

const getResponseMessage = (response) =>
  response && typeof response === 'object' && response.message
    ? response.message
    : response;

const normalizeLanguageKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const parseLanguageEntries = (value) => {
  const seen = new Set();

  return String(value || '')
    .split(languageSplitPattern)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) return false;
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

function getResumeDisplayFileName(fileName) {
  if (!fileName) return '';
  return fileName.replace(resumeFilePrefixPattern, '');
}

export class InterpreterSignUpsForm extends Component {
  static displayName = InterpreterSignUpsForm.name;

  constructor(props) {
    super(props);

    this.user = JSON.parse(GetCookie('user'));
    this.state = {
      model: { ...props.model },
      alert: props.alert,
      deltaPosition: { x: 0, y: 0 },
      statusDropdownOpen: false,
    };

    this.onSave = this.onSave.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.keydownHandler = this.keydownHandler.bind(this);
    this.confirmDelete = this.confirmDelete.bind(this);
    this.handleSaveEventSuccess = this.handleSaveEventSuccess.bind(this);
    this.handleResumeFileClick = this.handleResumeFileClick.bind(this);
    this.toggleStatusDropdown = this.toggleStatusDropdown.bind(this);
    this.handleStatusSelect = this.handleStatusSelect.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.keydownHandler);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.createdInterpreterId &&
      this.props.createdInterpreterId !== prevProps.createdInterpreterId
    ) {
      this.handleInterpreterCreated(this.props.createdInterpreterId);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.keydownHandler);
  }

  onStart = () => {};

  onStop = () => {};

  handleDrag = (e, ui) => {
    const { deltaPosition } = this.state;
    this.setState({
      deltaPosition: {
        x: deltaPosition.x + ui.deltaX,
        y: deltaPosition.y + ui.deltaY,
      },
    });
  };

  handleSaveEventSuccess(alert) {
    this.setState(
      {
        alert: {
          color: alert.color,
          message: alert.message,
          isOpen: true,
        },
      },
      () => {
        window.setTimeout(() => {
          this.setState({ alert: { isOpen: false } });
        }, Timeout(alert.message));
      },
    );
  }

  handleChange(e) {
    let model = { ...this.state.model };

    if (e.target.type === 'checkbox') model[e.target.name] = e.target.checked;
    else model[e.target.name] = e.target.value;

    this.setState({ model });
  }

  keydownHandler(e) {
    if (
      e.keyCode === 83 &&
      e.altKey &&
      this.props.modal &&
      !this.props.modalInterpreter
    ) {
      // Alt+S
      e.preventDefault();
      e.stopPropagation();
      this.onSave();
    }
  }

  handleResumeFileClick(e) {
    e.preventDefault();
    const fullResumeFileName = this.state.model.IntResumeFileName || '';
    if (!fullResumeFileName || !this.props.handleResumeDownload) return;

    this.props.handleResumeDownload(
      fullResumeFileName,
      getResumeDisplayFileName(fullResumeFileName),
    );
  }

  toggleStatusDropdown() {
    this.setState((prevState) => ({
      statusDropdownOpen: !prevState.statusDropdownOpen,
    }));
  }

  handleStatusSelect(value) {
    const model = { ...this.state.model, IntSignUpStatus: value };
    this.setState({
      model,
      statusDropdownOpen: false,
    });
  }

  async handleInterpreterCreated(interpreterId) {
    const languageEntries = parseLanguageEntries(this.state.model.IntLanguages);
    if (languageEntries.length <= 0) return;

    if (this.props.setFetchingData) {
      this.props.setFetchingData(true);
    }

    const failedLanguages = [];
    const existingLanguageKeys = new Set();

    try {
      const existingLanguages = await FetchData(
        '/getinterpreterlanguages',
        'POST',
        this.user.token,
        { IntID: interpreterId },
      );

      if (Array.isArray(existingLanguages)) {
        existingLanguages.forEach((item) => {
          const existingLanguageName = item?.intLanguage ?? item?.IntLanguage;
          const key = normalizeLanguageKey(existingLanguageName);
          if (key) existingLanguageKeys.add(key);
        });
      }
    } catch (error) {
      // Continue; we'll still try inserting and report per-language failures.
    }

    try {
      for (const language of languageEntries) {
        const languageKey = normalizeLanguageKey(language);
        if (existingLanguageKeys.has(languageKey)) {
          continue;
        }

        const payload = {
          EventType: 'insert',
          IntLangID: null,
          IntID: interpreterId,
          IntLanguage: language,
          CreateDateTime: null,
          CreateByUserId: this.user.userid,
          UpdateByUserId: null,
          UpdateDateTime: null,
          IsDeletedFlg: false,
        };

        try {
          const response = await FetchData(
            '/saveinterpreterlanguage',
            'POST',
            this.user.token,
            payload,
          );
          const message = getResponseMessage(response);

          if (message !== 'Success') {
            failedLanguages.push({
              language,
              message: message || 'Unknown error',
            });
          } else {
            existingLanguageKeys.add(languageKey);
          }
        } catch (error) {
          failedLanguages.push({
            language,
            message: 'Unexpected error while saving this language.',
          });
        }
      }
    } finally {
      if (this.props.setFetchingData) {
        this.props.setFetchingData(false);
      }
    }

    if (failedLanguages.length > 0) {
      const failedMessages = failedLanguages
        .map((item) => `${item.language}: ${item.message}`)
        .join('; ');

      this.handleSaveEventSuccess({
        color: 'warning',
        message: `Interpreter ${interpreterId} created, but some languages failed to save. ${failedMessages}`,
      });
      return;
    }

    this.handleSaveEventSuccess({
      color: 'success',
      message: `Interpreter ${interpreterId} created and languages were added.`,
    });
  }

  onSave() {
    let model = { ...this.state.model };

    if (!model.IntFirstName || !model.IntLastName || !model.IntEmail) {
      this.handleSaveEventSuccess({
        message: 'First Name, Last Name, and Email are required.',
        modal: true,
        color: 'warning',
      });
      return;
    }

    if (model.EventType === 'insert') {
      model.CreateByUserId = this.user.userid;
    } else if (model.EventType === 'update') {
      model.UpdateByUserId = this.user.userid;
    }

    FetchData('/saveinterpretersignup', 'POST', this.user.token, model).then(
      (data) => {
        const message = data && data.message ? data.message : data;
        if (message === 'Success') {
          this.props.handleSaveEventSuccess({
            message: message,
            modal: false,
            color: 'success',
            model: model,
          });
        } else {
          this.handleSaveEventSuccess({
            message: message || 'Unable to save interpreter signup.',
            modal: true,
            color: 'warning',
          });
        }
      },
    );
  }

  confirmDelete() {
    if (!this.state.model.IntSignUpId) return;

    if (
      window.confirm('Are you sure you want to delete this Interpreter Signup?')
    ) {
      FetchData('/deleteinterpretersignup', 'POST', this.user.token, {
        IntSignUpId: this.state.model.IntSignUpId,
        UpdateByUserId: this.user.userid,
      }).then((data) => {
        const message = data && data.message ? data.message : data;
        if (message === 'Success') {
          this.props.handleSaveEventSuccess({
            message: 'Interpreter Signup has been deleted successfully',
            modal: false,
            color: 'success',
            model: this.state.model,
          });
        } else {
          this.handleSaveEventSuccess({
            message: message || 'Unable to delete interpreter signup.',
            modal: true,
            color: 'warning',
          });
        }
      });
    }
  }

  render() {
    const dragHandlers = { onStart: this.onStart, onStop: this.onStop };
    const { deltaPosition } = this.state;

    return (
      <Draggable handle=".handle" onDrag={this.handleDrag} {...dragHandlers}>
        <div>
          <Modal
            isOpen={this.props.modal}
            toggle={this.props.setModal}
            style={{
              transform: `translate(${deltaPosition.x}px, ${deltaPosition.y}px)`,
              width: '920px',
            }}
            backdrop="static"
          >
            <ModalHeader className="handle">
              New/Edit Interpreter Signup
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col>
                  <Alert
                    color={this.state.alert.color}
                    isOpen={this.state.alert.isOpen}
                  >
                    <center>{this.state.alert.message}</center>
                  </Alert>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText>First Name:</InputGroupText>
                    <input
                      type="text"
                      name="IntFirstName"
                      className="form-control form-control-text"
                      value={this.state.model.IntFirstName || ''}
                      maxLength={50}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText>Last Name:</InputGroupText>
                    <input
                      type="text"
                      name="IntLastName"
                      className="form-control form-control-text"
                      value={this.state.model.IntLastName || ''}
                      maxLength={50}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Email:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntEmail"
                      className="form-control form-control-text"
                      value={this.state.model.IntEmail || ''}
                      maxLength={100}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Phone:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntPhone"
                      className="form-control form-control-text"
                      value={this.state.model.IntPhone || ''}
                      maxLength={30}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Languages:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntLanguages"
                      className="form-control form-control-text"
                      value={this.state.model.IntLanguages || ''}
                      maxLength={255}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Status:
                    </InputGroupText>
                    <div style={{ flex: 1 }}>
                      <Dropdown
                        isOpen={this.state.statusDropdownOpen}
                        toggle={this.toggleStatusDropdown}
                      >
                        <DropdownToggle
                          caret
                          size="sm"
                          color="light"
                          className="form-control text-start"
                        >
                          {this.state.model.IntSignUpStatus || 'Select Status'}
                        </DropdownToggle>
                        <DropdownMenu style={{ width: '100%' }}>
                          {signUpStatusOptions.map((status) => (
                            <DropdownItem
                              key={status}
                              onClick={() => this.handleStatusSelect(status)}
                            >
                              {status}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </InputGroup>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Address:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntStreet"
                      className="form-control form-control-text"
                      value={this.state.model.IntStreet || ''}
                      maxLength={100}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      City:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntCity"
                      className="form-control form-control-text"
                      value={this.state.model.IntCity || ''}
                      maxLength={50}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      State:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntState"
                      className="form-control form-control-text"
                      value={this.state.model.IntState || ''}
                      maxLength={20}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Zip:
                    </InputGroupText>
                    <input
                      type="text"
                      name="IntZip"
                      className="form-control form-control-text"
                      value={this.state.model.IntZip || ''}
                      maxLength={20}
                      onChange={this.handleChange}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Row>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Sign Up Date:
                    </InputGroupText>
                    <input
                      type="date"
                      name="IntSignUpDate"
                      className="form-control form-control-text"
                      style={{ backgroundColor: '#e9ecef' }}
                      value={
                        this.state.model.IntSignUpDate
                          ? moment(this.state.model.IntSignUpDate).format(
                              'YYYY-MM-DD',
                            )
                          : ''
                      }
                      disabled
                    />
                  </InputGroup>
                </Col>
                <Col className="col-6">
                  <InputGroup size="sm">
                    <InputGroupText style={{ width: '84px' }}>
                      Resume File:
                    </InputGroupText>
                    <div
                      className="form-control form-control-text"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {this.state.model.IntResumeFileName ? (
                        <button
                          type="button"
                          onClick={this.handleResumeFileClick}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            color: 'blue',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            font: 'inherit',
                          }}
                          title={this.state.model.IntResumeFileName}
                        >
                          {getResumeDisplayFileName(
                            this.state.model.IntResumeFileName,
                          )}
                        </button>
                      ) : (
                        ''
                      )}
                    </div>
                  </InputGroup>
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              {this.state.model.IntSignUpId && (
                <Button
                  color="danger"
                  className="btn btn-danger btn-secondary-no-background"
                  onClick={this.confirmDelete}
                >
                  Delete Application
                </Button>
              )}
              <Button
                color="secondary"
                className="btn btn-secondary-no-background"
                onClick={() => {
                  if (this.props.handleOpenInterpreterForm) {
                    this.props.handleOpenInterpreterForm(this.state.model);
                  }
                }}
              >
                Create New Interpreter
              </Button>
              <Button
                color="secondary"
                className="btn btn-secondary-no-background"
                onClick={this.props.setModal}
              >
                Close
              </Button>
              <Button
                color="primary"
                className="btn btn-secondary-no-background"
                onClick={this.onSave}
              >
                Save
              </Button>
            </ModalFooter>
          </Modal>
        </div>
      </Draggable>
    );
  }
}
